import { useEffect, useMemo, useState } from 'react';
import { IBoeingLine } from '@entities/boeing/IBoeingLine';
import { IBoeingLineAccounts } from '@entities/boeing/IBoeingLineAccounts';
import { useBoeingLineGlobalHashRate } from '@app/queries/boeing/useBoeingLineGlobalHashRate';
import { useBoeingLineHashRate } from '@app/queries/boeing/useBoeingLineHashRate';
import { useBoeingLineReward } from '@app/queries/boeing/useBoeingLineReward';
import { useBoeingLineViaBtcHashRate } from '@app/queries/boeing/useBoeingLineViaBtcHashRate';
import { useBoeingLineLostHashRate } from '@app/queries/boeing/useBoeingLineLostHashRate';
import { useBoeingLineIncreasedHashRate } from '@app/queries/boeing/useBoeingLineIncreasedHashRate';
import { useBoeingLineLeftAccounts } from '@app/queries/boeing/useBoeingLineLeftAccounts';
import { useBoeingLineNewAccounts } from '@app/queries/boeing/useBoeingLineNewAccounts';
import { IMainIndicatorsFilter } from '@pages/Main/components/Filter';
import { halfOfYearLabelRange } from '@app/helpers/lineChartsHelper';

function useMainGraph(
  dynamicsGraphGlobalHashRates: boolean,
  dynamicsGraphHashRates: boolean,
  dynamicsGraphViaBtcHashRates: boolean,
  dynamicsGraphReward: boolean,
  dynamicsGraphLostHashRate: boolean,
  dynamicsGraphIncreaseHashRate: boolean,
  dynamicsGraphHashRateOutflow: boolean,
  dynamicsGraphHashRateInflow: boolean,
  filters?: IMainIndicatorsFilter | null
): [boolean, any[]] {
  const boeingLineGlobalHashRateFetcher = useBoeingLineGlobalHashRate();
  const boeingLineHashRateFetcher = useBoeingLineHashRate();
  const boeingLineRewardFetcher = useBoeingLineReward();
  const boeingLineViaBtcHashRateFetcher = useBoeingLineViaBtcHashRate();
  const boeingLineLostHashRateFetcher = useBoeingLineLostHashRate();
  const boeingLineIncreasedHashRateFetcher = useBoeingLineIncreasedHashRate();

  const boeingLineLeftAccountsFetcher = useBoeingLineLeftAccounts();
  const boeingLineNewAccountsFetcher = useBoeingLineNewAccounts();

  const [lineGlobalHashRates, setLineGlobalHashRates] = useState<IBoeingLine[] | null>(null);
  const [lineHashRates, setLineHashRates] = useState<IBoeingLine[] | null>(null);
  const [lineViaBtcHashRates, setLineViaBtcHashRates] = useState<IBoeingLine[] | null>(null);
  const [lineReward, setLineReward] = useState<IBoeingLine[] | null>(null);
  const [lineLineLostHashRate, setLineLostHashRate] = useState<IBoeingLine[] | null>(null);
  const [lineIncreasedHashRate, setIncreasedHashRate] = useState<IBoeingLine[] | null>(null);

  const [lineLeftAccounts, setLineLeftAccounts] = useState<IBoeingLineAccounts[] | null>(null);
  const [lineNewAccounts, setLineNewAccounts] = useState<IBoeingLineAccounts[] | null>(null);

  useEffect(() => {
    (async () => {
      if (!filters || !dynamicsGraphGlobalHashRates) return;

      const items = await boeingLineGlobalHashRateFetcher.fetch(filters);
      setLineGlobalHashRates(items);
    })();
  }, [filters, dynamicsGraphGlobalHashRates]);

  useEffect(() => {
    (async () => {
      if (!filters || !dynamicsGraphHashRates) return;

      const items = await boeingLineHashRateFetcher.fetch(filters);
      setLineHashRates(items);
    })();
  }, [filters, dynamicsGraphHashRates]);

  useEffect(() => {
    (async () => {
      if (!filters || !dynamicsGraphReward) return;

      const items = await boeingLineRewardFetcher.fetch(filters);
      setLineReward(items);
    })();
  }, [filters, dynamicsGraphReward]);

  useEffect(() => {
    (async () => {
      if (!filters || !dynamicsGraphViaBtcHashRates) return;

      const items = await boeingLineViaBtcHashRateFetcher.fetch(filters);
      setLineViaBtcHashRates(items);
    })();
  }, [filters, dynamicsGraphViaBtcHashRates]);

  useEffect(() => {
    (async () => {
      if (!filters || !dynamicsGraphLostHashRate) return;

      const items = await boeingLineLostHashRateFetcher.fetch(filters);
      setLineLostHashRate(items);
    })();
  }, [filters, dynamicsGraphLostHashRate]);

  useEffect(() => {
    (async () => {
      if (!filters || !dynamicsGraphIncreaseHashRate) return;

      const items = await boeingLineIncreasedHashRateFetcher.fetch(filters);
      setIncreasedHashRate(items);
    })();
  }, [filters, dynamicsGraphIncreaseHashRate]);

  useEffect(() => {
    (async () => {
      if (!filters || !dynamicsGraphHashRateInflow) return;

      const items = await boeingLineNewAccountsFetcher.fetch(filters);
      setLineNewAccounts(items);
    })();
  }, [filters, dynamicsGraphHashRateInflow]);

  useEffect(() => {
    (async () => {
      if (!filters || !dynamicsGraphHashRateOutflow) return;

      const items = await boeingLineLeftAccountsFetcher.fetch(filters);
      setLineLeftAccounts(items);
    })();
  }, [filters, dynamicsGraphHashRateOutflow]);

  const formatDynamicsGraph = (
    data: any,
    items: { date: string; value: number }[],
    indicator: string
  ) => {
    let currMonth = '';
    let isSkippedFirstIndicator = false;
    const monthForHalfOfYear = halfOfYearLabelRange(items);

    items.map(item => {
      const [, month, year] = item.date.split('-');
      let label = null;
      if (null !== monthForHalfOfYear) {
        if (currMonth !== month && monthForHalfOfYear.includes(+month)) {
          label = month + '.' + year.slice(-2);
          currMonth = month;
        }
      } else {
        if (currMonth !== month) {
          label = month + '.' + year.slice(-2);
          currMonth = month;
        }
      }

      if (!isSkippedFirstIndicator && label) {
        label = null;
        isSkippedFirstIndicator = true;
      }

      // @ts-ignore
      data[item.date] = Object.assign(data[item.date] !== undefined ? data[item.date] : {}, {
        name: label,
        Дата: item.date,
        value: item.value,
        [indicator]: item.value,
      });
    });

    return data;
  };

  const formatDynamicsGraphAccounts = (
    data: any,
    items: {
      date: string;
      hashrate: number;
      accounts: number;
      clients: number;
    }[]
  ) => {
    let currMonth = '';
    let isSkippedFirstIndicator = false;
    const monthForHalfOfYear = halfOfYearLabelRange(items);

    items.map(item => {
      const [, month, year] = item.date.split('-');
      let label = null;
      if (null !== monthForHalfOfYear) {
        if (currMonth !== month && monthForHalfOfYear.includes(+month)) {
          label = month + '.' + year.slice(-2);
          currMonth = month;
        }
      } else {
        if (currMonth !== month) {
          label = month + '.' + year.slice(-2);
          currMonth = month;
        }
      }

      if (!isSkippedFirstIndicator && label) {
        label = null;
        isSkippedFirstIndicator = true;
      }

      // @ts-ignore
      data[item.date] = Object.assign(data[item.date] !== undefined ? data[item.date] : {}, {
        name: label,
        Дата: item.date,
        accounts_hashrate: item.hashrate,
        accounts_accounts: item.accounts,
        accounts_clients: item.clients,
      });
    });

    return data;
  };

  const graphData = useMemo(() => {
    let data: never[] = [];

    if (dynamicsGraphHashRateOutflow && lineLeftAccounts && lineLeftAccounts.length) {
      data = formatDynamicsGraphAccounts(data, lineLeftAccounts);
    }

    if (dynamicsGraphHashRateInflow && lineNewAccounts && lineNewAccounts.length) {
      data = formatDynamicsGraphAccounts(data, lineNewAccounts);
    }

    if (dynamicsGraphGlobalHashRates && lineGlobalHashRates && lineGlobalHashRates.length) {
      data = formatDynamicsGraph(data, lineGlobalHashRates, 'globalHashRates');
    }

    if (dynamicsGraphHashRates && lineHashRates && lineHashRates.length) {
      data = formatDynamicsGraph(data, lineHashRates, 'hashRates');
    }

    if (dynamicsGraphViaBtcHashRates && lineViaBtcHashRates && lineViaBtcHashRates.length) {
      data = formatDynamicsGraph(data, lineViaBtcHashRates, 'viaBtcHashRates');
    }

    if (dynamicsGraphReward && lineReward && lineReward.length) {
      data = formatDynamicsGraph(data, lineReward, 'reward');
    }

    if (dynamicsGraphLostHashRate && lineLineLostHashRate && lineLineLostHashRate.length) {
      data = formatDynamicsGraph(data, lineLineLostHashRate, 'lostHashRate');
    }

    if (dynamicsGraphIncreaseHashRate && lineIncreasedHashRate && lineIncreasedHashRate.length) {
      data = formatDynamicsGraph(data, lineIncreasedHashRate, 'increaseHashRate');
    }

    return Object.values(data);
  }, [
    lineGlobalHashRates,
    lineHashRates,
    lineViaBtcHashRates,
    lineReward,
    lineLineLostHashRate,
    lineIncreasedHashRate,
    lineLeftAccounts,
    lineNewAccounts,
    dynamicsGraphGlobalHashRates,
    dynamicsGraphHashRates,
    dynamicsGraphViaBtcHashRates,
    dynamicsGraphReward,
    dynamicsGraphLostHashRate,
    dynamicsGraphIncreaseHashRate,
    dynamicsGraphHashRateOutflow,
    dynamicsGraphHashRateInflow,
  ]);

  const loading = !(
    (boeingLineGlobalHashRateFetcher.isSuccess && lineGlobalHashRates !== null) ||
    (boeingLineHashRateFetcher.isSuccess && lineHashRates !== null) ||
    (boeingLineRewardFetcher.isSuccess && lineViaBtcHashRates !== null) ||
    (boeingLineViaBtcHashRateFetcher.isSuccess && lineReward !== null) ||
    (boeingLineLostHashRateFetcher.isSuccess && lineLineLostHashRate !== null) ||
    (boeingLineIncreasedHashRateFetcher.isSuccess && lineIncreasedHashRate !== null) ||
    (boeingLineLeftAccountsFetcher.isSuccess && lineLeftAccounts !== null) ||
    (boeingLineNewAccountsFetcher.isSuccess && lineNewAccounts !== null)
  );

  return [loading, graphData];
}

export default useMainGraph;
